import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, unwrap } from "@/lib/api";
import type { Me } from "@/types/api";

export function useMe() {
  return useQuery({
    queryKey: ["me"],
    queryFn: async () => {
      const { data } = await api.get("/auth/me");
      return unwrap<Me>(data);
    },
    staleTime: 60_000,
    retry: false,
  });
}

/**
 * RBAC permission check for the current user. `can(routeKey, "write")` →
 * may act (approve/edit) on that page; `"read"` → may view. Full-access users
 * and the loading state return true (fail-open — never hide before we know).
 */
export function useCan() {
  const me = useMe();
  const fullAccess = me.data?.fullAccess ?? true;
  const perms = me.data?.permissions ?? [];
  return (routeKey: string, level: "read" | "write" = "read") => {
    if (!me.data || fullAccess) return true;
    if (level === "read") return perms.includes(`${routeKey}:read`) || perms.includes(`${routeKey}:write`);
    return perms.includes(`${routeKey}:write`);
  };
}

export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { email: string; password: string; totpCode?: string }) => {
      const { data } = await api.post("/auth/login", vars);
      return unwrap<{ token: string; user: Partial<Me> }>(data);
    },
    onSuccess: async () => {
      // /auth/login returns a slim user; invalidate so the next render
      // pulls the full Me payload from /auth/me.
      await qc.invalidateQueries({ queryKey: ["me"] });
    },
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await api.post("/auth/logout", {});
      return true;
    },
    onSuccess: () => {
      qc.removeQueries({ queryKey: ["me"] });
      qc.clear();
    },
  });
}

export function useRegister() {
  return useMutation({
    mutationFn: async (vars: {
      fullName: string;
      email: string;
      phone: string;
      gender?: string;
      dateOfBirth?: string;
      country?: string;
      state?: string;
      city?: string;
      zipCode?: string;
      address?: string;
      password: string;
      otpChannel: "email" | "whatsapp" | "telegram";
    }) => {
      const { data } = await api.post("/auth/register", vars);
      return unwrap<{ userId: string; email: string; otpChannel: string; message: string }>(data);
    },
  });
}

/** Combined register-as-IB — creates user + ib_application in one POST. */
export function useRegisterIb() {
  return useMutation({
    mutationFn: async (vars: {
      fullName: string;
      email: string;
      phone: string;
      gender?: string;
      dateOfBirth?: string;
      country?: string;
      state?: string;
      city?: string;
      zipCode?: string;
      address?: string;
      password: string;
      otpChannel: "email" | "whatsapp" | "telegram";
      businessName?: string;
      businessType?: "sole_prop" | "company" | "partnership" | "individual";
      websiteUrl?: string;
      expectedMonthlyVolumeUsd?: number;
      marketingPlan?: string;
      declaredSponsorCode?: string;
    }) => {
      const { data } = await api.post("/auth/register-ib", vars);
      return unwrap<{ userId: string; email: string; otpChannel: string; applicationId: number; existing?: boolean; message: string }>(data);
    },
  });
}

export function useActivate() {
  return useMutation({
    mutationFn: async (vars: { email: string; code: string }) => {
      const { data } = await api.post("/auth/activate", vars);
      return unwrap<{ activated: boolean }>(data);
    },
  });
}

export function useResendOtp() {
  return useMutation({
    mutationFn: async (vars: { email: string; purpose?: "activation" | "password_reset" }) => {
      const { data } = await api.post("/auth/otp/resend", vars);
      return unwrap<{ sent: boolean }>(data);
    },
  });
}

/** Forgot-password: complete the reset with the emailed OTP + new password. */
export function useResetPassword() {
  return useMutation({
    mutationFn: async (vars: { email: string; code: string; newPassword: string }) => {
      const { data } = await api.post("/auth/reset-password", vars);
      return unwrap<{ reset: boolean }>(data);
    },
  });
}

/** Admin: set a new password for a trader/IB/manager directly. */
export function useSetUserPassword() {
  return useMutation({
    mutationFn: async (vars: { id: number; newPassword: string }) => {
      const { data } = await api.post(`/admin/users/${vars.id}/set-password`, { newPassword: vars.newPassword });
      return unwrap<{ id: number; email: string; updated: boolean }>(data);
    },
  });
}

/** Admin: impersonate ("log in as") a user. Swaps the browser session. */
export function useImpersonate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const { data } = await api.post(`/admin/users/${id}/impersonate`);
      return unwrap<{ impersonating: { id: number; email: string; fullName: string; role: string } }>(data);
    },
    onSuccess: async () => { await qc.invalidateQueries({ queryKey: ["me"] }); },
  });
}

/** End an impersonated session and restore the admin's own session. */
export function useStopImpersonation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.post("/auth/stop-impersonation", {});
      return unwrap<{ restored: boolean }>(data);
    },
    onSuccess: () => { qc.clear(); },
  });
}
